import classNames from 'classnames';
import gql from 'graphql-tag';
import I18n from 'i18n-js';
import { get } from 'lodash';
import React from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { ShopBadges } from '@reverbdotcom/commons/src/components/shop_badges';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';

import DEFAULT from '../../../../images/default-avatar.jpg';
import { CoreShopContainer } from '../../../schemas/rql';
import FreeShippingCallout from './free_shipping_callout';
import URLHelpers, { adminEditUserPath, shopPromotePath } from '../../shared/url_helpers';
import FavoriteShopCTA from '../../../../src/components/favorites/favorite_shop_cta';

interface IProps extends IUserContext {
  shop: CoreShopContainer.Shop;
  initialFavoriteId: string | null;
}

export const ShopHeaderFragment = gql`
  fragment ShopHeaderFragment on Shop {
    _id
    id
    slug
    name
    preferredSeller
    quickShipper
    quickResponder
    description
    userUuid
    userId
    address {
      displayLocation
    }
    banner {
      source
    }
    user {
      _id
      id
      avatar {
        source
      }
    }
  }
`;

class ShopHeader extends React.Component<IProps, null> {
  getMessageURL() {
    return URLHelpers.newDashboardMessagesPath(null, this.props.shop.userUuid);
  }

  renderBanner() {
    const url = get(this.props.shop, 'banner.source');
    if (!url) return null;

    return (
      <div
        className="shop-headerv2__banner"
      >
        <img src={url} alt={this.props.shop.name} />
      </div>
    );
  }

  renderAvatar() {
    const url = get(this.props.shop, 'user.avatar.source', false);

    return <img src={url || DEFAULT} alt={this.props.shop.name} />;
  }

  renderAdminLink() {
    if (!this.props.user.isAdmin) return null;

    return (
      <a
        href={adminEditUserPath.expand({ id: this.props.shop.userId })}
        data-edit-link
      >
        <i className="fa fa-magic ml-1" />
      </a>
    );
  }

  shouldRenderFreeShippingCallout() {
    const { user, shop } = this.props;

    return !!shop.freeDomesticShippingRate &&
      shop.freeDomesticShippingRate.regionCode === user.shippingRegionCode;
  }

  renderOwnerLinks() {
    if (`${this.props.user.id}` !== this.props.shop.userId) return null;

    return (
      <ul className="mt-half">
        <li className="d-inline-block mtb-half mlr-half">
          <CoreLink
            to={URLHelpers.shopEditPath()}
            className="button button--small"
          >
            {I18n.t('discovery.shopHeader.editShop')}
          </CoreLink>
        </li>
        <li className="d-inline-block mtb-half mlr-half">
          <CoreLink
            to={shopPromotePath}
            className="button button--small"
          >
            {I18n.t('discovery.shopHeader.promoteShop')}
          </CoreLink>
        </li>
      </ul>
    );
  }

  renderPlaceholder() {
    const classes = classNames(
      'shop-headerv2',
      'shop-headerv2--placeholder',
      'shop-headerv2--with-banner',
    );

    return (
      <div className={classes}>
        <div className="shop-headerv2__banner" />
        <div className="shop-headerv2__main">
          <div className="shop-headerv2__main__avatar" />
          <div className="shop-headerv2__main__text" />
        </div>
      </div>
    );
  }

  render() {
    const { shop } = this.props;
    if (!shop) return this.renderPlaceholder();

    const classes = classNames(
      'shop-headerv2',
      { 'shop-headerv2--with-banner': !!this.renderBanner() },
    );

    return (
      <div className={classes}>
        {this.renderBanner()}
        <div className="shop-headerv2__main">
          <div className="shop-headerv2__main__avatar">
            {this.renderAvatar()}
          </div>
          <div className="shop-headerv2__main__text">
            <h1 className="shop-headerv2__title">
              {shop.name}
              <FavoriteShopCTA
                shop={shop}
                initialFavoriteId={this.props.initialFavoriteId}
                parentComponentName="ShopHeader"
              />
              {this.renderAdminLink()}
            </h1>
            <ul className="shop-headerv2__details">
              <li>
                <span className="icon-l-map-marker">
                  {shop.address.displayLocation}
                </span>
              </li>
              <li>
                <a
                  href={this.getMessageURL()}
                  className="icon-l-comment-o"
                >
                  {I18n.t('discovery.shopHeader.message')}
                </a>
              </li>
            </ul>
            <div className="shop-headerv2__bio">
              <SanitizedRender
                html={shop.description}
                truncationLimit={200}
                htmlTag="div"
                convertNewlines
              />
            </div>
            <>
              {this.shouldRenderFreeShippingCallout() && <FreeShippingCallout shop={shop} />}
              {this.renderOwnerLinks()}
            </>
          </div>
          <div className="shop-headerv2__main__badges">
            <ShopBadges
              preferredSeller={shop.preferredSeller}
              quickResponder={shop.quickResponder}
              quickShipper={shop.quickShipper}
              stacked
            />
          </div>
        </div>
      </div>
    );
  }
}

export default withUserContext(ShopHeader);
